import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { BUProfile } from 'shared/models/buProfile';
import { PagedResponse } from 'shared/models/paged-response';
import { ProfileSearchOptions } from 'shared/models/profile-search-options';
import { FormDataService } from 'src/app/shared/services/formData.service';
import { Affiliation } from '../../shared/models/affiliation';
import { Agency } from '../../shared/models/agency';
import { BUSCode } from '../../shared/models/busCode';
import { ChangeHistory } from '../../shared/models/changeHistory';
import { CPDFCode } from '../../shared/models/cpdfCode';
import { Guid } from '../../shared/models/guid';
import { Union } from '../../shared/models/union';

declare var baseApiUrl: string;

@Injectable({
  providedIn: 'root'
})
export class ProfileService {

  constructor(private http: HttpClient, private formDataService: FormDataService) {

  }

  public get(id: string): Observable<BUProfile> {

    var requestUrl = `${baseApiUrl}/profiles/${id}`;

    return this.http.get<BUProfile>(requestUrl).pipe(
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public getChangeHistory(profileId: string): Observable<ChangeHistory[]> {
    const requestUrl = `${baseApiUrl}/changehistory/${profileId}`;
    return this.http.get<ChangeHistory[]>(requestUrl).pipe(
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public save(profile: BUProfile): Observable<BUProfile> {

    const id = (profile && profile.id) ? profile.id : Guid.empty;
    let requestUrl = `${baseApiUrl}/profiles/update/${id}`;

    if (profile.isNew == true) {
      requestUrl = `${baseApiUrl}/profiles/create`;
    }

    const formData: FormData = this.formDataService.createFormData(profile);

    return this.http.post<BUProfile>(requestUrl, formData).pipe(
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public saveChangeHistory(changeHistory: ChangeHistory): Observable<ChangeHistory> {
    const requestUrl = `${baseApiUrl}/changehistory/create`;
    return this.http.post<ChangeHistory>(requestUrl, changeHistory).pipe(
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public saveBusCode(busCode: BUSCode): Observable<BUSCode> {
    const id = (busCode && busCode.id) ? busCode.id : Guid.empty;

    let requestUrl = `${baseApiUrl}/bus-codes/update/${id}`;
    if (id === Guid.empty)
      requestUrl = `${baseApiUrl}/bus-codes/create`;
    return this.http.post<BUSCode>(requestUrl, busCode).pipe(
      tap((response) => console.log('Response:', response)),
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public saveCpdfCode(cpdfCode: CPDFCode): Observable<CPDFCode> {
    const id = (cpdfCode && cpdfCode.id) ? cpdfCode.id : Guid.empty;

    let requestUrl = `${baseApiUrl}/cpdf-codes/update/${id}`;
    if (id === Guid.empty)
      requestUrl = `${baseApiUrl}/cpdf-codes/create`;
    return this.http.post<CPDFCode>(requestUrl, cpdfCode).pipe(
      tap((response) => console.log('Response:', response)),
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public saveUnion(union: Union): Observable<Union> {
    const id = (union && union.id) ? union.id : Guid.empty;

    let requestUrl = `${baseApiUrl}/unions/update/${id}`;
    if (id === Guid.empty)
      requestUrl = `${baseApiUrl}/unions/create`;
    return this.http.post<Union>(requestUrl, union).pipe(
      tap((response) => console.log('Response:', response)),
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public search(searchOptions: ProfileSearchOptions): Observable<PagedResponse<BUProfile>> {

    const agencies = searchOptions.filters.agencies.map(s => "agencies=" + s).join("&");
    const unions = searchOptions.filters.unions.map(s => "unions=" + s).join("&");
    const busCodes = searchOptions.filters.busCodes.map(s => "busCodes=" + s).join("&");
    //const cpdfCodes = searchOptions.filters.cpdfCodes.map(s => "cpdfCodes=" + s).join("&");
    let requestUrl = "";
    if (searchOptions.filters.isAdmin)
      requestUrl = `${baseApiUrl}/profiles/admin-get-profiles?searchTerm=${searchOptions.filters.searchTerm}&pageSize=${searchOptions.paging.itemsPerPage}&currentPage=${searchOptions.paging.currentPage}&sortBy=${searchOptions.paging.sortBy}&isDesc=${searchOptions.paging.isDesc}`;
    else
      requestUrl = `${baseApiUrl}/profiles/get-profiles?searchTerm=${searchOptions.filters.searchTerm}&pageSize=${searchOptions.paging.itemsPerPage}&currentPage=${searchOptions.paging.currentPage}&sortBy=${searchOptions.paging.sortBy}&isDesc=${searchOptions.paging.isDesc}`;
    if (agencies.length > 0) {
      requestUrl += "&" + agencies
    }

    if (unions.length > 0) {
      requestUrl += "&" + unions
    }

    if (busCodes.length > 0) {
      requestUrl += "&" + busCodes
    }

    //if (cpdfCodes.length > 0) {
    //	requestUrl += "&" + cpdfCodes
    //}

    return this.http.get<PagedResponse<BUProfile>>(requestUrl).pipe(
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public createSearchQuery(searchOptions: ProfileSearchOptions): string {

    const agencies = searchOptions.filters.agencies.map(s => "agencies=" + s).join("&");
    const unions = searchOptions.filters.unions.map(s => "unions=" + s).join("&");
    //const cpdfCodes = searchOptions.filters.cpdfCodes.map(s => "cpdfCodes=" + s).join("&");

    let query = `searchTerm=${searchOptions.filters.searchTerm}&pageSize=${searchOptions.paging.itemsPerPage}&currentPage=${searchOptions.paging.currentPage}&sortBy=${searchOptions.paging.sortBy}&isDesc=${searchOptions.paging.isDesc}`;
    if (agencies.length > 0) {
      query += "&" + agencies
    }

    if (unions.length > 0) {
      query += "&" + unions
    }

    //if (cpdfCodes.length > 0) {
    //	query += "&" + cpdfCodes
    //}

    return query;
  }

  public getAgencies(): Observable<string[]> {

    var requestUrl = `${baseApiUrl}/agencies`;

    return this.http.get<string[]>(requestUrl).pipe(
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public getAgencyObjects(): Observable<Agency[]> {

    var requestUrl = `${baseApiUrl}/agencies/GetObjects`;

    return this.http.get<Agency[]>(requestUrl).pipe(
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public getUnions(): Observable<string[]> {

    var requestUrl = `${baseApiUrl}/unions`;

    return this.http.get<string[]>(requestUrl).pipe(
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public getUnionObjects(): Observable<Union[]> {

    var requestUrl = `${baseApiUrl}/unions/GetObjects`;

    return this.http.get<Union[]>(requestUrl).pipe(
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public getOlmrNumbers(): Observable<string[]> {

    var requestUrl = `${baseApiUrl}/olmr-numbers`;

    return this.http.get<string[]>(requestUrl).pipe(
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public getCpdfCodes(): Observable<string[]> {

    var requestUrl = `${baseApiUrl}/cpdf-codes`;

    return this.http.get<string[]>(requestUrl).pipe(
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public getCpdfCodeObjects(): Observable<CPDFCode[]> {

    var requestUrl = `${baseApiUrl}/cpdf-codes/GetObjects`;

    return this.http.get<CPDFCode[]>(requestUrl).pipe(
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public getBusCodes(): Observable<string[]> {

    var requestUrl = `${baseApiUrl}/bus-codes`;

    return this.http.get<string[]>(requestUrl).pipe(
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public getBusCodeObjects(): Observable<BUSCode[]> {

    var requestUrl = `${baseApiUrl}/bus-codes/GetObjects`;

    return this.http.get<BUSCode[]>(requestUrl).pipe(
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  public getAffiliationObjects(): Observable<Affiliation[]> {

    var requestUrl = `${baseApiUrl}/affiliations/GetObjects`;

    return this.http.get<Affiliation[]>(requestUrl).pipe(
      catchError((httpError: HttpErrorResponse) => {
        return this.handleError(httpError);
      })
    );
  }

  isBusCodeInUse(busCode: string): Observable<boolean> {
    return this.http.get<boolean>(`${baseApiUrl}/bus-codes/BusCodeExists/${busCode}`);
  }

  handleError(httpError: HttpErrorResponse): Observable<never> {
    var errorMessage = "An unknown error occurred while submitting the request.";

    if (httpError.error && httpError.error.message) {
      errorMessage = httpError.error.message;
    }

    return throwError(httpError);
  }
}
